import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const EditPrescription = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  // Pagination and Search State for patients
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const prevPatient = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch: refetchPatients,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const { data: medicines, isLoading: isLoadingMedicines } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token
  );

  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: auth?.user?.user?.id || "",
    patient: null, // Changed to null
    duration: "",
    discount: 0,
    total_price: 0,
    items: [],
    extra_medicines: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const dosageRefs = useRef([]);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);
  const timesPer24Refs = useRef([]);
  const [initialPrescriptionData, setInitialPrescriptionData] = useState(null);
  const [prescriptionLoaded, setPrescriptionLoaded] = useState(false); // Track if prescription data is loaded

  // Fetch the current prescription details
  const {
    data: prescriptionData,
    loading: loadingPrescription,
    error: prescriptionError,
  } = useFetchObjects(["prescription"], `prescriptions/${id}`, token);

  const {
    handleUpdate: handleUpdatePrescription,
    resetForm,
    loading,
  } = useUpdate("prescriptions", token, -1);

  // Error handling for prescription fetch
  useEffect(() => {
    if (prescriptionError) {
      console.error("Error fetching prescription:", prescriptionError);
    }
  }, [prescriptionError, t]);

  // Populate patients list
  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    }
  }, [patientsData]);

  // Initialize patient search
  useEffect(() => {
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, [searchTerm, refetchPatients]);

  // Populate form with fetched prescription data
  useEffect(() => {
    if (prescriptionData && medicines && !prescriptionLoaded) {
      setInitialPrescriptionData(prescriptionData);

      // Transform items to include medicine details
      const itemsWithDetails =
        prescriptionData.drugs?.map((item) => {
          const medicine = medicines.find((m) => m.id === item.stock_medicine);
          return {
            ...item,
            stock_medicine: item.stock_medicine,
            name:
              medicine?.medicine_display ||
              item.stock_medicine_display ||
              item.name,
            available_stock: medicine?.quantity || item.available_stock,
            sell_price: medicine?.sell_price || medicine?.sell_price || 0,
            total_price:
              (medicine?.sell_price || item.sell_price) * item.quantity,
            dosage: item.dosage,
            quantity: item.quantity,
            notation: item.notation,
            is_before: item.is_before,
            units_per_meal: item.units_per_meal,
            times_per_24: item.times_per_24,
            id: item.id,
          };
        }) || [];

      setInput({
        date: prescriptionData.date || getCurrentDate(),
        time: prescriptionData.time || getCurrentTime(),
        doctor: prescriptionData.doctor || auth?.user?.user?.id || "",
        patient: null, // Ensure it's initially null or patient object
        duration: prescriptionData.duration || "",
        discount: prescriptionData.discount || 0,
        total_price: prescriptionData.total_price || 0,
        items: itemsWithDetails || [],
        extra_medicines: prescriptionData.extra_medicines || [],
      });

      // Find the current patient in the existing list
      if (prescriptionData?.patient) {
        const currentPatient = allPatients.find(
          (p) => p.id === prescriptionData.patient
        );

        if (currentPatient) {
          setInput((prev) => ({ ...prev, patient: currentPatient }));
          setPatientInputValue(
            `${currentPatient.firstname} ${
              currentPatient.lastname || ""
            } (ID: ${currentPatient.id}, Phone: ${
              currentPatient?.phone || "N/A"
            })`.trim()
          );
          prevPatient.current = currentPatient;
        } else {
          // If patient not found in existing list, fetch specifically
          fetchPatientAndSet(prescriptionData.patient);
        }
      } else {
        setIsInitialLoad(false);
      }

      setPrescriptionLoaded(true); // Mark prescription data as loaded
    }
  }, [prescriptionData, medicines, allPatients, auth, prescriptionLoaded]);

  // After update, always use backend's total_price for display - only on initial load
  useEffect(() => {
    if (prescriptionData && !prescriptionLoaded) {
      const backendTotalPrice = parseInt(prescriptionData.total_price) || 0;
      setInput((prev) => ({
        ...prev,
        total_price: backendTotalPrice,
      }));
    }
  }, [prescriptionData, prescriptionLoaded]);

  // Handle discount changes
  const handleDiscountChange = (e) => {
    const discountValue = parseInt(e.target.value) || 0;
    const newTotalPrice = Math.max(calculateTotalPrice(input.items) - discountValue, 0);
    setInput((prev) => ({
      ...prev,
      discount: discountValue,
      total_price: newTotalPrice
    }));
  };

  // Function to fetch a specific patient by ID
  const fetchPatientAndSet = async (patientId) => {
    try {
      const response = await fetch(`patients/${patientId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const patientData = await response.json();
        setInput((prev) => ({ ...prev, patient: patientData }));
        setPatientInputValue(
          `${patientData.firstname} ${patientData.lastname || ""} (ID: ${
            patientData.id
          }, Phone: ${patientData?.phone || "N/A"})`.trim()
        );
        prevPatient.current = patientData;
        // Add to allPatients list if not already present
        setAllPatients((prev) => {
          if (!prev.find((p) => p.id === patientData.id)) {
            return [...prev, patientData];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error fetching patient:", error);
    } finally {
      setIsInitialLoad(false);
    }
  };

  // Reset form after successful update
  useEffect(() => {
    if (resetForm) {
      navigate(-1);
    }
  }, [resetForm, navigate]);

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const handleChangePrescriptionField = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const newItem = {
        stock_medicine: medicine.id,
        name: medicine.medicine_display,
        quantity: 1,
        dosage: 1,
        sell_price: medicine.sell_price,
        total_price: medicine.sell_price,
        available_stock: medicine.quantity,
        notation: "",
        is_before: "after",
        units_per_meal: 1,
        times_per_24: 1,
      };
      
      const updatedItems = [...input.items, newItem];
      const newTotalPrice = calculateTotalPrice(updatedItems);
      
      setInput((prevInput) => ({
        ...prevInput,
        items: updatedItems,
        total_price: newTotalPrice,
      }));
      
      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    
    // Parse the value correctly
    const parsedValue = value === "" ? "" : name === "notation" || name === "is_before" ? value : parseInt(value) || 0;
    items[index][name] = parsedValue;
    
    // Update the individual item's total price
    if (name === "quantity" || name === "sell_price") {
      items[index].total_price = items[index].sell_price * items[index].quantity;
      const newTotalPrice = Math.max(calculateTotalPrice(items) - (input.discount || 0), 0);
      setInput((prev) => ({
        ...prev,
        items,
        total_price: newTotalPrice,
      }));
    } else {
      setInput((prev) => ({
        ...prev,
        items,
      }));
    }
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (field === "dosage" && unitsPerMealRefs.current[index]) {
        unitsPerMealRefs.current[index].focus();
      } else if (field === "units_per_meal" && timesPer24Refs.current[index]) {
        timesPer24Refs.current[index].focus();
      } else if (field === "times_per_24" && quantityRefs.current[index]) {
        quantityRefs.current[index].focus();
      } else if (field === "quantity" && notationRefs.current[index]) {
        notationRefs.current[index].focus();
      } else if (field === "notation" && dosageRefs.current[index + 1]) {
        dosageRefs.current[index + 1].focus();
      }
    }
  };

  const addExtraItem = () => {
    setInput((prevInput) => ({
      ...prevInput,
      extra_medicines: [
        ...prevInput.extra_medicines,
        {
          title: "",
          dosage: "",
          quantity: "",
          notation: "",
          units_per_meal: "",
          times_per_24: "",
          is_before: "",
        },
      ],
    }));
  };

  const removeExtraItem = (index) => {
    setInput((prevInput) => ({
      ...prevInput,
      extra_medicines: prevInput.extra_medicines.filter((_, i) => i !== index),
    }));
  };

  const handleExtraItemChange = (index, field, value) => {
    const newExtra = [...input.extra_medicines];
    newExtra[index][field] = value;
    setInput((prev) => ({ ...prev, extra_medicines: newExtra }));
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    const newTotalPrice = Math.max(calculateTotalPrice(items) - (input.discount || 0), 0);
    setInput((prev) => ({
      ...prev,
      items,
      total_price: newTotalPrice
    }));
  };

  const calculateTotalPrice = (items) =>
    items.reduce((sum, item) => sum + (item.sell_price * item.quantity || 0), 0);

  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setInput((prev) => ({ ...prev, patient: newValue }));
    if (newValue) {
      prevPatient.current = newValue;
    }
  };

  const handleBlur = () => {
    if (!input.patient && prevPatient.current) {
      setInput((prev) => ({ ...prev, patient: prevPatient.current }));
      setPatientInputValue(
        `${prevPatient.current.firstname} ${
          prevPatient.current.lastname || ""
        } (ID: ${prevPatient.current.id}, Phone: ${
          prevPatient.current?.phone || "N/A"
        })`.trim()
      );
    }
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }
    if (!input.doctor) {
      errors.doctor = t("pharmacy.doctorRequired");
      isValid = false;
    }
    if (!input.patient) {
      errors.patient = t("pharmacy.patientRequired");
      isValid = false;
    }

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
      if (!item.sell_price) {
        errors[`sell_price_${index}`] = t("pharmacy.priceRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);

    if (isValid) {
      const updateData = {
        ...input,
        patient: input.patient?.id,
        total_price: input.total_price, // Send the already calculated total price
        items: input.items.map((item) => ({
          stock_medicine: item.stock_medicine,
          dosage: item.dosage || 0,
          quantity: item.quantity || 0,
          notation: item.notation || "",
          units_per_meal: item.units_per_meal || 0,
          times_per_24: item.times_per_24 ?? null,
          is_before: item.is_before || "after",
          id: item.id,
        })),
      };
      
      handleUpdatePrescription(id, updateData);
    }
  };

  // Only recalculate total price when user explicitly changes an item
  const recalculateTotalPrice = (items) => {
    return items.reduce(
      (sum, item) => sum + (item.sell_price * item.quantity || 0),
      0
    );
  };

  // Patient Autocomplete Handlers
  const getOptionLabel = (option) => {
    return option
      ? `${option.firstname} ${option.lastname || ""} (ID: ${
          option.id
        }, Phone: ${option?.phone || "N/A"})`.trim()
      : "";
  };

  useEffect(() => {
    if (
      prescriptionData?.patient &&
      patientsData?.results &&
      !input.patient &&
      !isLoadingPatients
    ) {
      const initialPatient = patientsData.results.find(
        (p) => p.id === prescriptionData.patient
      );
      if (initialPatient) {
        setInput((prev) => ({ ...prev, patient: initialPatient }));
        setPatientInputValue(getOptionLabel(initialPatient));
        prevPatient.current = initialPatient;
      }
    }
  }, [prescriptionData, patientsData, isLoadingPatients]);

  const autocompleteValue = input.patient || null;

  if (loadingPrescription) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (prescriptionError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        color="red"
      >
        <Typography variant="h6">
          {t("pharmacy.prescriptionLoadError")}
        </Typography>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("pharmacy.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("pharmacy.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("pharmacy.editPrescriptionTitle", "Edit Prescription")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.prescriptionDate")}
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={input.date}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.date}
                helperText={formErrors.date}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.time")}
                name="time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={input.time}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.time}
                helperText={formErrors.time}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={allPatients}
                getOptionLabel={getOptionLabel}
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                onBlur={handleBlur}
                value={autocompleteValue}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
                loading={isLoadingPatients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("pharmacy.patient")}
                    variant="outlined"
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingPatients ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.duration")}
                name="duration"
                variant="outlined"
                value={input.duration}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.duration}
                helperText={formErrors.duration}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.discount","Discount")}
                name="discount"
                type="number"
                variant="outlined"
                value={input.discount || 0}
                onChange={handleDiscountChange}
                InputProps={{
                  inputProps: { min: 0 }
                }}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.addMedicine")}
          </Typography>
          <Autocomplete
            ref={autocompleteRef}
            value={null}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) =>
              setAutocompleteInputValue(newInputValue)
            }
            onChange={(event, value) => {
              handleAddNewItem(value);
              setAutocompleteInputValue("");
            }}
            options={
              medicines
                ? medicines.filter((medicine) =>
                    medicine.medicine_display
                      .toLowerCase()
                      .startsWith(autocompleteInputValue.toLowerCase())
                  )
                : []
            }
            getOptionLabel={(option) =>
              `${option.medicine_display} (${option.category_display})`
            }
            loading={isLoadingMedicines}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "#eef2f5" },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", color: "text.primary" }}
                >
                  {option.medicine_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
                >
                  <strong>{t("pharmacy.category")}:</strong>{" "}
                  {option.category_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
                >
                  <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
                >
                  <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                  {option.expiry_date}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.searchMedicine")}
                variant="outlined"
                placeholder={t("pharmacy.searchMedicinePlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingMedicines ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            )}
          />
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("pharmacy.medicineName")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.availableStock")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.dosage")}</TableCell>
                <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                  {t("pharmacy.unitsPerMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.timesPer24")}</TableCell>
                <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                <TableCell align="center">{t("pharmacy.price")}</TableCell>
                <TableCell align="center">{t("pharmacy.notation")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.isBeforeMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
                <TableCell align="center">{t("pharmacy.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {input.items?.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{item.available_stock}</TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (dosageRefs.current[index] = el)}
                      name="dosage"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.dosage}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "dosage")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (unitsPerMealRefs.current[index] = el)}
                      name="units_per_meal"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.units_per_meal}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, index, "units_per_meal")
                      }
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (timesPer24Refs.current[index] = el)}
                      name="times_per_24"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.times_per_24 ?? ""}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "times_per_24")}
                      inputProps={{ min: 1, max: 24 }}
                      placeholder="1"
                      sx={{ backgroundColor: "#fff", borderRadius: 2, width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (quantityRefs.current[index] = el)}
                      name="quantity"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.quantity}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "quantity")}
                      error={!!formErrors[`quantity_${index}`]}
                      helperText={formErrors[`quantity_${index}`]}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (priceRefs.current[index] = el)}
                      name="sell_price"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.sell_price}
                      onChange={(e) => handleChangeItemField(index, e)}
                      disabled
                      sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (notationRefs.current[index] = el)}
                      name="notation"
                      type="text"
                      size="small"
                      variant="outlined"
                      value={item.notation}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "notation")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl fullWidth size="small">
                      <Select
                        value={item.is_before}
                        onChange={(e) => handleChangeItemField(index, e)}
                        name="is_before"
                        ref={(el) => (isBeforeRefs.current[index] = el)}
                      >
                        <MenuItem value="before">
                          {t("pharmacy.before")}
                        </MenuItem>
                        <MenuItem value="med">{t("pharmacy.med")}</MenuItem>
                        <MenuItem value="after">{t("pharmacy.after")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={600}>{item.total_price}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("pharmacy.removeItem")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container>
          {input.extra_medicines?.map((item, index) => (
            <Grid
              container
              justifyContent="space-between"
              gap={2}
              mt={2}
              key={index}
            >
              <TextField
                name="title"
                label={t("pharmacy.medicineName")}
                type="text"
                size="small"
                variant="outlined"
                value={item.title}
                onChange={(e) =>
                  handleExtraItemChange(index, "title", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="quantity"
                label={t("pharmacy.quantity")}
                type="text"
                size="small"
                variant="outlined"
                value={item.quantity}
                onChange={(e) =>
                  handleExtraItemChange(index, "quantity", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="dosage"
                label={t("pharmacy.dosage")}
                type="text"
                size="small"
                variant="outlined"
                value={item.dosage}
                onChange={(e) =>
                  handleExtraItemChange(index, "dosage", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="notation"
                label={t("pharmacy.notation")}
                type="text"
                size="small"
                variant="outlined"
                value={item.notation}
                onChange={(e) =>
                  handleExtraItemChange(index, "notation", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="units_per_meal"
                label={t("pharmacy.unitsPerMeal")}
                type="text"
                size="small"
                variant="outlined"
                value={item.units_per_meal}
                onChange={(e) =>
                  handleExtraItemChange(index, "units_per_meal", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="times_per_24"
                label={t("pharmacy.timesPer24")}
                type="text"
                size="small"
                variant="outlined"
                value={item.times_per_24 ?? ""}
                onChange={(e) =>
                  handleExtraItemChange(index, "times_per_24", e.target.value)
                }
                placeholder="1"
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <TextField
                name="is_before"
                label={t("pharmacy.isBeforeMeal")}
                type="text"
                size="small"
                variant="outlined"
                value={item.is_before}
                onChange={(e) =>
                  handleExtraItemChange(index, "is_before", e.target.value)
                }
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
              <IconButton onClick={() => removeExtraItem(index)} color="error">
                <RemoveCircleOutline />
              </IconButton>
            </Grid>
          ))}
        </Grid>

        <Button variant="outlined" onClick={addExtraItem} sx={{ mt: 1 }}>
          {t("pharmacy.addExtraMedicines", "Add Extra Medicines")}
        </Button>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <TextField
            size="small"
            label={t("pharmacy.totalPrice")}
            type="number"
            value={input.total_price}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
            disabled
          />
          <TextField
            size="small"
            label={t("pharmacy.discount")}
            type="number"
            name="discount"
            value={input.discount || 0}
            onChange={handleDiscountChange}
            InputProps={{
              inputProps: { min: 0 }
            }}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
          />
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 700,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EditPrescription;
